import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ZoomService } from './zoom.service';

export interface ChatMessage {
  id: string;
  senderId: number;
  senderName: string;
  message: string;
  timestamp: Date;
  isSelf: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  private unreadCountSubject = new BehaviorSubject<number>(0);
  
  public messages$: Observable<ChatMessage[]> = this.messagesSubject.asObservable();
  public unreadCount$: Observable<number> = this.unreadCountSubject.asObservable();
  
  private chatClient: any = null;
  private currentUserId: number | null = null;
  private isInitialized = false;

  constructor(private zoomService: ZoomService) {}

  /**
   * Initialize the chat service with the Zoom client
   */
  async initializeChat(): Promise<void> {
    try {
      const client = this.zoomService.client;
      if (!client) {
        throw new Error('Zoom client not initialized');
      }

      this.chatClient = client.getChatClient();
      this.currentUserId = client.getCurrentUserInfo()?.userId || null;
      
      // Listen for incoming chat messages
      client.on('chat-on-message', (payload: any) => {
        console.log('Chat message received:', payload);
        this.handleIncomingMessage(payload);
      });

      this.isInitialized = true;
      console.log('Chat service initialized successfully');
    } catch (error) {
      console.error('Error initializing chat service:', error);
      throw error;
    }
  }

  /**
   * Send a chat message to a specific user or all participants
   * @param message The message text to send
   * @param receiverId Optional user ID to send to. If not provided, sends to all.
   */
  async sendMessage(message: string, receiverId?: number): Promise<void> {
    if (!this.chatClient) {
      throw new Error('Chat client not initialized');
    }

    if (!message.trim()) {
      return;
    }

    try {
      // Send to specific user or all participants
      const targetId = receiverId || (await this.getOtherParticipantId());
      
      console.log('Sending chat message to user:', targetId, 'Message:', message);
      await this.chatClient.send(message, targetId);
      
      // Add sent message locally - Zoom SDK may not trigger event for sender
      const client = this.zoomService.client;
      if (client) {
        const currentUser = client.getCurrentUserInfo();
        let senderName = currentUser?.userIdentity || 'Unknown';
        const normalizedIdentity = senderName.toLowerCase();
        
        if (normalizedIdentity === 'coordinator') {
          senderName = 'Coordinator';
        } else if (normalizedIdentity === 'inspector') {
          senderName = 'Inspector';
        }
        
        const sentMessage: ChatMessage = {
          id: `${Date.now()}-sent`,
          senderId: this.currentUserId || 0,
          senderName: senderName,
          message: message,
          timestamp: new Date(),
          isSelf: true // This is our own sent message
        };
        
        console.log('✅ Adding sent message (isSelf=true):', sentMessage);
        this.addMessage(sentMessage);
      }
      
      console.log('Chat message sent successfully');
    } catch (error) {
      console.error('Error sending chat message:', error);
      throw error;
    }
  }

  /**
   * Handle incoming chat messages
   */
  private handleIncomingMessage(payload: any): void {
    console.log('📨 Handling incoming chat message - Full payload:', JSON.stringify(payload, null, 2));
    const { message, sender, timestamp } = payload;
    
    // Check if this is from the current user (skip if we already added it locally)
    const isSelf = sender.userId === this.currentUserId;
    
    // Skip if this message was already added locally by the sender
    if (isSelf) {
      console.log('⚠️ Skipping own message - already added locally');
      return;
    }
    
    // Get the sender's identity from Zoom client's user list
    const client = this.zoomService.client;
    let senderName = 'Unknown';
    
    if (client) {
      const allUsers = client.getAllUser();
      const senderUser = allUsers.find(u => u.userId === sender.userId);
      console.log('🔍 Found sender in user list:', senderUser);
      
      if (senderUser) {
        const identity = senderUser.displayName || senderUser.userIdentity || '';
        const normalizedIdentity = identity.toLowerCase();
        
        console.log('👤 Sender identity from user list:', identity, 'normalized:', normalizedIdentity);
        
        if (normalizedIdentity === 'coordinator') {
          senderName = 'Coordinator';
        } else if (normalizedIdentity === 'inspector') {
          senderName = 'Inspector';
        } else {
          senderName = identity || 'Unknown';
        }
      } else {
        // Fallback to sender object
        const identity = sender.displayName || sender.userIdentity || sender.name || '';
        const normalizedIdentity = identity.toLowerCase();
        
        console.log('👤 Sender identity from payload:', identity, 'normalized:', normalizedIdentity);
        
        if (normalizedIdentity === 'coordinator') {
          senderName = 'Coordinator';
        } else if (normalizedIdentity === 'inspector') {
          senderName = 'Inspector';
        } else {
          senderName = identity || 'Unknown';
        }
      }
    }
    
    const chatMessage: ChatMessage = {
      id: `${sender.userId}-${timestamp || Date.now()}`,
      senderId: sender.userId,
      senderName: senderName,
      message: message,
      timestamp: timestamp ? new Date(timestamp) : new Date(),
      isSelf: false // This is a received message, not from self
    };

    console.log('✅ Adding received message (isSelf=false):', chatMessage);
    this.addMessage(chatMessage);
    
    // Increment unread count for messages from others
    const newCount = this.unreadCountSubject.value + 1;
    console.log('📊 Incrementing unread count to:', newCount);
    this.unreadCountSubject.next(newCount);
  }

  /**
   * Add a message to the chat history
   */
  private addMessage(message: ChatMessage): void {
    const currentMessages = this.messagesSubject.value;
    this.messagesSubject.next([...currentMessages, message]);
  }

  /**
   * Get the ID of the other participant (for 1-on-1 chat)
   */
  private async getOtherParticipantId(): Promise<number> {
    const client = this.zoomService.client;
    if (!client) {
      throw new Error('Zoom client not initialized');
    }

    const allUsers = client.getAllUser();
    const otherUser = allUsers.find(user => user.userId !== this.currentUserId);
    
    if (!otherUser) {
      throw new Error('No other participant found');
    }

    return otherUser.userId;
  }

  /**
   * Get all chat messages
   */
  getMessages(): ChatMessage[] {
    return this.messagesSubject.value;
  }

  /**
   * Get the most recent message
   */
  getLatestMessage(): ChatMessage | null {
    const messages = this.messagesSubject.value;
    return messages.length > 0 ? messages[messages.length - 1] : null;
  }

  /**
   * Mark all messages as read
   */
  markAsRead(): void {
    this.unreadCountSubject.next(0);
  }

  /**
   * Clear all chat messages
   */
  clearMessages(): void {
    this.messagesSubject.next([]);
    this.unreadCountSubject.next(0);
  }

  /**
   * Clean up when leaving session
   */
  cleanup(): void {
    this.clearMessages();
    this.chatClient = null;
    this.currentUserId = null;
    this.isInitialized = false;
  }

  /**
   * Check if chat is initialized
   */
  get initialized(): boolean {
    return this.isInitialized;
  }
}

